import { jsx as _jsx } from "react/jsx-runtime";
import { TreeSelect, } from "primereact/treeselect";
import React, { useMemo, useRef } from "react";
import "./hierarchicalMultiSelect.css"; // Keep for any overrides; can prune later
export const HierarchicalMultiSelect = ({ hierarchy, value, onChange, placeholder = "Select practices / sites", practiceLabel = "Practices", siteLabel = "Sites", disabled, overlayWidth = 360, className = "", }) => {
    // Build PrimeReact TreeSelect nodes
    const treeData = useMemo(() => {
        return hierarchy.practices.map((p) => ({
            key: p.id,
            label: p.name,
            data: { type: "practice" },
            children: p.sites.map((s) => ({
                key: `${p.id}::${s.id}`,
                label: s.name,
                data: { type: "site", practiceId: p.id, siteId: s.id },
            })),
        }));
    }, [hierarchy.practices]);
    // Build selection keys structure for TreeSelect (checkbox mode uses object map)
    const treeSelection = useMemo(() => {
        const keys = {};
        const siteIdSet = new Set(value.siteIds);
        const practiceIdSet = new Set(value.practiceIds);
        hierarchy.practices.forEach((p) => {
            const allSites = p.sites.map((s) => s.id);
            const selectedSites = allSites.filter((id) => siteIdSet.has(id));
            const allSelected = selectedSites.length === allSites.length && allSites.length > 0;
            if (allSelected || practiceIdSet.has(p.id)) {
                // treat as fully selected only if every site selected
                if (allSelected) {
                    keys[p.id] = { checked: true };
                    allSites.forEach((sid) => (keys[`${p.id}::${sid}`] = { checked: true }));
                }
                else if (selectedSites.length > 0) {
                    // partial - some sites selected; keep practice NOT fully checked
                    keys[p.id] = { partialChecked: true };
                    selectedSites.forEach((sid) => (keys[`${p.id}::${sid}`] = { checked: true }));
                }
            }
            else if (selectedSites.length > 0) {
                // sites selected but not all -> partial
                keys[p.id] = { partialChecked: true };
                selectedSites.forEach((sid) => (keys[`${p.id}::${sid}`] = { checked: true }));
            }
        });
        return keys;
    }, [hierarchy.practices, value.practiceIds, value.siteIds]);
    const handleChange = (e) => {
        const raw = e.value;
        const keys = (raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {});
        const practiceIdsFull = [];
        const siteIds = [];
        const practiceIdsWithAny = new Set();
        // Pass 1 gather selections
        Object.entries(keys).forEach(([k, meta]) => {
            //for Sites
            if (k.includes("::")) {
                const [pid, sid] = k.split("::");
                if (meta.checked) {
                    siteIds.push(sid);
                    practiceIdsWithAny.add(pid);
                }
            }
            else if (meta.checked) {
                // Practice fully checked (all sites should be captured)
                practiceIdsFull.push(k);
            }
        });
        // Determine which practices are fully selected (all their sites present)
        hierarchy.practices.forEach((p) => {
            const allSiteIds = p.sites.map((s) => s.id);
            if (allSiteIds.length && allSiteIds.every((sid) => siteIds.includes(sid))) {
                if (!practiceIdsFull.includes(p.id))
                    practiceIdsFull.push(p.id);
            }
        });
        // Represent practiceIds as ANY selected sites or fully selected; dedupe via set union
        const union = new Set([...practiceIdsWithAny, ...practiceIdsFull]);
        onChange({ practiceIds: Array.from(union), siteIds });
    };
    const label = useMemo(() => {
        if (!value.practiceIds.length && !value.siteIds.length)
            return placeholder;
        const practiceCount = value.practiceIds.length;
        const siteCount = value.siteIds.length;
        return `${practiceCount} practice${practiceCount !== 1 ? "s" : ""}, ${siteCount} site${siteCount !== 1 ? "s" : ""}`;
    }, [value.practiceIds.length, value.siteIds.length, placeholder]);
    // Custom value template: show counts only (no individual name chips only count)
    const valueTemplate = () => label;
    const treeSelectRef = useRef(null); //ref to TreeSelect instance for hiding
    // Programmatic close with graceful fallback.
    const closePanel = () => {
        const inst = treeSelectRef.current;
        // Preferred: call component's internal hide (if PrimeReact exposes it)
        if (inst && typeof inst.hide === "function") {
            inst.hide();
            return;
        }
        // If PrimeReact does not react (unlikely), ensure local cleanup so footer disappears.
    };
    return (_jsx("div", { className: "psp-treeselect-holder", children: _jsx(TreeSelect, { className: `w-full psp-treeselect-compact ${className}`, value: treeSelection, onChange: handleChange, options: treeData, placeholder: placeholder, selectionMode: "checkbox", metaKeySelection: false, filter: true, filterPlaceholder: `Search ${practiceLabel} & ${siteLabel}`, disabled: disabled, valueTemplate: valueTemplate, "aria-label": "Practice and Site multi-select tree", ref: treeSelectRef, showClear: true, closeIcon: false }) }));
};
